import { GroupSizeField, RoomsSplitField } from '@koob/ui';
import { FolderChildrenAgesField } from './FolderChildrenAgesField';
import { toDateString } from '@koob/dates';
import {
  ChildAgeModeEnum,
  type FolderFormValues,
  type ChildEntry,
  type FolderTravellersSectionProps,
} from './types';
import {
  rebalanceRooms,
  remapTravellers,
  estimateBirthdate,
  normaliseChildAge,
} from './folderFormState';

export type { FolderTravellersSectionProps };

export function FolderTravellersSection({
  values,
  setFieldValue,
  t,
}: FolderTravellersSectionProps) {
  const handleAdultsChange = (n: number) => {
    setFieldValue('adults', n);
    // Traveller records are positional: without re-indexing, every child would
    // slide onto another record when the adults count moves.
    setFieldValue(
      'travelers',
      remapTravellers(
        values.travelers,
        values.adults,
        n,
        values.children.length,
      ),
    );
    setFieldValue(
      'rooms',
      rebalanceRooms(n, values.children.length, values.rooms),
    );
  };

  const handleChildrenCountChange = (n: number) => {
    const nextChildren: ChildEntry[] = Array.from(
      { length: n },
      (_, i) =>
        values.children[i] ?? {
          mode: ChildAgeModeEnum.DATE,
          birthdate: null,
          age: null,
          ageIsExact: undefined,
        },
    );
    setFieldValue('children', nextChildren);
    // Removed children take their traveller record with them.
    setFieldValue(
      'travelers',
      remapTravellers(values.travelers, values.adults, values.adults, n),
    );
    setFieldValue('rooms', rebalanceRooms(values.adults, n, values.rooms));
  };

  const updateChild = (i: number, entry: ChildEntry) => {
    const nextChildren = [...values.children];
    nextChildren[i] = entry;
    setFieldValue('children', nextChildren);

    const travelerIndex = values.adults + i;
    const existingTraveler = values.travelers[travelerIndex] ?? {};
    const nextTravelers = [...values.travelers];
    nextTravelers[travelerIndex] = {
      ...existingTraveler,
      birthdate: entry.birthdate ?? undefined,
      age: entry.age ?? undefined,
      ageIsExact: entry.ageIsExact,
    };
    setFieldValue('travelers', nextTravelers);
  };

  const handleChildBirthdateChange = (i: number, d: Date | null) => {
    updateChild(i, {
      mode: ChildAgeModeEnum.DATE,
      birthdate: toDateString(d),
      age: null,
      ageIsExact: true,
    });
  };

  const handleChildAgeChange = (i: number, age: number | null) => {
    // A child is under 18: the input's max only guards the spinner, so the
    // typed value is bounded here, before anything downstream reads it.
    const childAge = normaliseChildAge(age);
    // Availability searches need a birthdate, so an age is turned into an
    // estimated one — ageIsExact keeps the distinction for the booking side.
    updateChild(i, {
      mode: ChildAgeModeEnum.AGE,
      birthdate: childAge === null ? null : estimateBirthdate(childAge),
      age: childAge,
      ageIsExact: false,
    });
  };

  const handleChildModeChange = (i: number, mode: ChildAgeModeEnum) => {
    updateChild(i, {
      mode,
      birthdate: null,
      age: null,
      ageIsExact: mode === ChildAgeModeEnum.DATE,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-base font-semibold text-gray-800">
          {t('groupSizeSection')} <span className="text-red-500">*</span>
        </h4>
        <p className="mt-1 text-sm text-gray-500">
          {t('groupSizeDescription')}
        </p>
        <div className="mt-4">
          <GroupSizeField
            adults={values.adults}
            childrenCount={values.children.length}
            onAdultsChange={handleAdultsChange}
            onChildrenCountChange={handleChildrenCountChange}
            t={t}
          />
        </div>
      </div>
      <FolderChildrenAgesField
        entries={values.children}
        onBirthdateChange={handleChildBirthdateChange}
        onAgeChange={handleChildAgeChange}
        onModeChange={handleChildModeChange}
        t={t}
      />
      <div>
        <h4 className="text-base font-semibold text-gray-800">
          {t('roomCompositionSection')}
        </h4>
        <p className="mt-1 text-sm text-gray-500">{t('rooms.description')}</p>
        <div className="mt-4">
          <RoomsSplitField
            adultsTotal={values.adults}
            childrenTotal={values.children.length}
            rooms={values.rooms}
            onRoomsChange={(rooms) => setFieldValue('rooms', rooms)}
            t={t}
          />
        </div>
      </div>
    </div>
  );
}
